/**
 * HeroSlider — Web Component para el hero-banner-slider de Hermes Music.
 * Reemplaza Swiper: loop infinito, drag táctil + escritorio, autoplay,
 * teclado, pausa en hover/focus, IntersectionObserver, ARIA, reduced-motion.
 * Sin dependencias externas. ~3KB minificado.
 */
class HeroSlider extends HTMLElement {
  /* ---- Estado privado ---- */
  #index      = 0;
  #total      = 0;
  #allSlides  = []; // incluye clones
  #realSlides = [];
  #track      = null;
  #viewport   = null;
  #dots       = [];
  #prevBtn    = null;
  #nextBtn    = null;
  #liveRegion = null;

  #autoplayDelay  = 0;
  #speed          = 600;
  #loop           = true;
  #parallax       = false;
  #isTransitioning = false;
  #autoplayTimer  = null;
  #isHovered      = false;
  #isFocused      = false;
  #isVisible      = true;
  #reduced        = false;

  #drag = { active: false, startX: 0, delta: 0, moved: false };
  #baseOffset  = 0;
  #resizeTimer = null;
  #sizeObserver = null;
  #observer    = null;

  // Cached layout measurements — updated only on init and resize (avoids forced reflow)
  #slideW = 0;
  #gapW   = 0;
  #vpW    = 0;

  /* ---- Lifecycle ---- */

  connectedCallback() {
    this.#track      = this.querySelector('.hero-slider__track');
    this.#viewport   = this.querySelector('.hero-slider__viewport');
    this.#realSlides = [...this.querySelectorAll('.hero-slider__slide:not(.hero-slider__slide--clone)')];
    this.#total      = this.#realSlides.length;

    if (!this.#track || this.#total === 0) return;

    this.#autoplayDelay = parseInt(this.dataset.autoplay  || '0');
    this.#speed         = parseInt(this.dataset.speed     || '600');
    this.#loop          = this.dataset.loop !== 'false' && this.#total > 1;
    this.#parallax      = this.dataset.parallax === 'true';
    this.#reduced       = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.#total === 1) {
      this.#realSlides[0].setAttribute('data-active', '');
      this.#triggerAnim(this.#realSlides[0]);
      this.setAttribute('data-ready', '');
      return;
    }

    if (this.#loop) this.#cloneEdges();
    this.#allSlides = [...this.#track.querySelectorAll('.hero-slider__slide')];
    this.#dots      = [...this.querySelectorAll('.hero-slider__dot')];
    this.#prevBtn   = this.querySelector('.hero-slider__prev');
    this.#nextBtn   = this.querySelector('.hero-slider__next');
    this.#liveRegion = this.querySelector('.hero-slider__live-region');

    const t0 = performance.now();
    console.log(`[HeroSlider] setup completo en ${t0.toFixed(1)}ms desde nav-start`);

    // Dos rAF para separar write y read de layout sin forced reflow:
    // - Frame 1: setAttribute('data-ready') cambia CSS gap de calc(...) a 20px.
    //   El browser aplica el nuevo CSS durante este frame.
    // - Frame 2: el CSS ya está calculado — offsetWidth y columnGap leen sin forzar
    //   un layout síncronamente (elimina ~150ms de forced reflow).
    requestAnimationFrame(() => {
      const tRaf = performance.now();
      console.log(`[HeroSlider] rAF1: ${(tRaf - t0).toFixed(1)}ms desde setup`);
      this.setAttribute('data-ready', '');          // WRITE: CSS gap → 20px

      requestAnimationFrame(() => {
        this.#measureLayout();                        // READ: layout ya calculado
        console.log(`[HeroSlider] layout → slideW=${this.#slideW}px  gapW=${this.#gapW}px  vpW=${this.#vpW}px  (${(performance.now()-t0).toFixed(0)}ms total)`);
        this.#setPosition(0, false);
        this.#bindEvents();
        this.#startAutoplay();
        this.#observeViewport();
        this.#observeSize();
        console.log(`[HeroSlider] operativo en ${(performance.now() - t0).toFixed(1)}ms total`);
      });
    });
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.#observer?.disconnect();
    this.#sizeObserver?.disconnect();
    window.removeEventListener('resize', this.#onResize);
  }

  /* ---- Setup ---- */

  #cloneEdges() {
    const cloneLast  = this.#realSlides.at(-1).cloneNode(true);
    const cloneFirst = this.#realSlides[0].cloneNode(true);
    for (const el of [cloneLast, cloneFirst]) {
      el.setAttribute('aria-hidden', 'true');
      el.classList.add('hero-slider__slide--clone');
    }
    this.#track.prepend(cloneLast);
    this.#track.append(cloneFirst);
    // Track DOM: [clone-last, slide-0, slide-1, ..., slide-N, clone-first]
    // realIndex → trackIndex = realIndex + 1
  }

  #bindEvents() {
    this.#prevBtn?.addEventListener('click', () => this.#onNav(() => this.prev()));
    this.#nextBtn?.addEventListener('click', () => this.#onNav(() => this.next()));
    this.#dots.forEach((dot, i) =>
      dot.addEventListener('click', () => this.#onNav(() => this.goTo(i)))
    );

    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  this.#onNav(() => this.prev());
      if (e.key === 'ArrowRight') this.#onNav(() => this.next());
    });

    this.addEventListener('mouseenter', () => { this.#isHovered = true;  this.#stopAutoplay(); });
    this.addEventListener('mouseleave', () => { this.#isHovered = false; this.#startAutoplay(); });
    this.addEventListener('focusin',    () => { this.#isFocused = true;  this.#stopAutoplay(); });
    this.addEventListener('focusout',   () => { this.#isFocused = false; this.#startAutoplay(); });

    this.#viewport.addEventListener('pointerdown',  this.#onPointerDown, { passive: true });
    this.#viewport.addEventListener('pointermove',  this.#onPointerMove, { passive: false });
    this.#viewport.addEventListener('pointerup',    this.#onPointerUp);
    this.#viewport.addEventListener('pointercancel', this.#onPointerUp);
    this.#viewport.addEventListener('dragstart', (e) => e.preventDefault());

    window.addEventListener('resize', this.#onResize, { passive: true });
  }

  #onNav(fn) { fn(); this.#restartAutoplay(); }

  /* ---- Posicionamiento ---- */

  // Batch-reads all layout properties at once. Call only before write phase
  // (connectedCallback rAF and resize handler) to avoid forced reflow.
  #measureLayout() {
    this.#slideW = this.#allSlides[1]?.offsetWidth || this.#allSlides[0]?.offsetWidth || 0;
    this.#gapW   = parseFloat(getComputedStyle(this.#track).columnGap) || 0;
    this.#vpW    = this.#viewport.offsetWidth;
  }

  #calcOffset(trackIndex) {
    return (this.#vpW - this.#slideW) / 2 - trackIndex * (this.#slideW + this.#gapW);
  }

  #applyOffset(offset, animate) {
    this.#track.style.transition = (animate && !this.#reduced)
      ? `transform ${this.#speed}ms cubic-bezier(0.4, 0, 0.2, 1)`
      : 'none';
    this.#track.style.transform = `translateX(${offset}px)`;
    this.#baseOffset = offset;
    if (this.#parallax) this.#applyParallax();
  }

  // realIndex: -1 = clone-last, 0..total-1 = real, total = clone-first
  #setPosition(realIndex, animate = true) {
    const ti = realIndex + 1; // track index (por el clone prepend)
    this.#applyOffset(this.#calcOffset(ti), animate);
    this.#updateClasses(ti);

    if (realIndex >= 0 && realIndex < this.#total) {
      this.#index = realIndex;
      this.#updateDots(realIndex);
      this.#announce(realIndex);
    }
  }

  #updateClasses(trackIndex) {
    for (const slide of this.#allSlides) {
      slide.removeAttribute('data-active');
      slide.removeAttribute('data-prev');
      slide.removeAttribute('data-next');
    }
    this.#allSlides[trackIndex]?.setAttribute('data-active', '');
    this.#allSlides[trackIndex - 1]?.setAttribute('data-prev', '');
    this.#allSlides[trackIndex + 1]?.setAttribute('data-next', '');
    this.#triggerAnim(this.#allSlides[trackIndex]);
  }

  #triggerAnim(slide) {
    if (!slide) return;
    for (const s of this.#allSlides) {
      s.querySelector('.banner-image-wrapper')?.classList.remove('animate-active');
    }
    setTimeout(() => {
      slide.querySelector('.banner-image-wrapper')?.classList.add('animate-active');
    }, 50);
  }

  #applyParallax() {
    const sw = this.#slideW, g = this.#gapW, vp = this.#vpW;
    const center = vp / 2;
    for (const [i, slide] of this.#allSlides.entries()) {
      const slideCenter = this.#baseOffset + i * (sw + g) + sw / 2;
      const progress = (slideCenter - center) / vp;
      const bg = slide.querySelector('.background-image');
      if (bg) bg.style.transform = `translateX(${progress * sw * 0.15}px)`;
    }
  }

  /* ---- Navegación pública ---- */

  next() {
    if (this.#isTransitioning) return;
    if (!this.#loop && this.#index >= this.#total - 1) return;

    if (this.#loop && this.#index === this.#total - 1) {
      // Animar a clone-first, luego saltar a real first sin animación
      this.#isTransitioning = true;
      this.#setPosition(this.#total, true);
      this.#afterTransition(() => { this.#setPosition(0, false); this.#isTransitioning = false; });
    } else {
      this.#setPosition(this.#index + 1, true);
    }
  }

  prev() {
    if (this.#isTransitioning) return;
    if (!this.#loop && this.#index <= 0) return;

    if (this.#loop && this.#index === 0) {
      // Animar a clone-last, luego saltar a real last sin animación
      this.#isTransitioning = true;
      this.#setPosition(-1, true);
      this.#afterTransition(() => { this.#setPosition(this.#total - 1, false); this.#isTransitioning = false; });
    } else {
      this.#setPosition(this.#index - 1, true);
    }
  }

  goTo(realIndex) {
    if (realIndex === this.#index || this.#isTransitioning) return;
    this.#setPosition(realIndex, true);
  }

  #afterTransition(fn) {
    const fallback = setTimeout(fn, this.#speed + 120);
    const onEnd = (e) => {
      // Ignorar transitionend de elementos hijos (slides tienen su propia transition).
      // Solo nos interesa el del track.
      if (e.target !== this.#track) return;
      this.#track.removeEventListener('transitionend', onEnd);
      clearTimeout(fallback);
      fn();
    };
    this.#track.addEventListener('transitionend', onEnd);
  }

  /* ---- Drag (touch + mouse) ---- */

  #onPointerDown = (e) => {
    if (e.button > 0) return;
    this.#drag = { active: true, startX: e.clientX, delta: 0, moved: false, pointerId: e.pointerId };
    this.#track.style.transition = 'none';
    this.#stopAutoplay();
  };

  #onPointerMove = (e) => {
    if (!this.#drag.active) return;
    const delta = e.clientX - this.#drag.startX;
    this.#drag.delta = delta;
    // Umbral de arrastre (slop): por debajo de 10px lo tratamos como click, no como
    // swipe. En escritorio es facil mover el mouse unos pixeles al hacer click; con un
    // umbral muy bajo ese micro-movimiento marcaba `moved` y #preventNextClick cancelaba
    // el click del enlace del banner (slide-link-overlay).
    if (!this.#drag.moved && Math.abs(delta) > 10) {
      this.#drag.moved = true;
      // Capturar el puntero SOLO cuando confirmamos un arrastre real, para no interferir
      // con los clicks normales sobre el enlace del slide.
      this.#viewport.setPointerCapture(this.#drag.pointerId);
    }
    // Mientras no sea un arrastre confirmado dejamos pasar el evento (es un click).
    if (!this.#drag.moved) return;
    // preventDefault() para evitar el scroll nativo una vez que ya es swipe.
    e.preventDefault();
    this.#track.style.transform = `translateX(${this.#baseOffset + delta}px)`;
    if (this.#parallax) this.#applyParallax();
  };

  #onPointerUp = () => {
    if (!this.#drag.active) return;
    const { delta, moved } = this.#drag;
    this.#drag.active = false;

    if (moved) {
      const threshold = this.#slideW * 0.22;
      this.#preventNextClick();
      if      (delta < -threshold) this.next();
      else if (delta >  threshold) this.prev();
      else this.#snapBack();
    }
    this.#startAutoplay();
  };

  #snapBack() {
    this.#track.style.transition = `transform ${this.#speed}ms cubic-bezier(0.4, 0, 0.2, 1)`;
    this.#track.style.transform  = `translateX(${this.#baseOffset}px)`;
  }

  #preventNextClick() {
    const block = (e) => { e.stopPropagation(); e.preventDefault(); };
    this.addEventListener('click', block, { once: true, capture: true });
  }

  /* ---- Autoplay ---- */

  #startAutoplay() {
    if (!this.#autoplayDelay || this.#reduced || this.#isHovered || this.#isFocused || !this.#isVisible) return;
    this.#stopAutoplay();
    this.#autoplayTimer = setTimeout(() => {
      if (!this.#drag.active) { this.next(); this.#startAutoplay(); }
    }, this.#autoplayDelay);
  }

  #stopAutoplay()    { clearTimeout(this.#autoplayTimer); }
  #restartAutoplay() { this.#stopAutoplay(); this.#startAutoplay(); }

  /* ---- UI ---- */

  #updateDots(realIndex) {
    for (const [i, dot] of this.#dots.entries()) {
      dot.classList.toggle('is-active', i === realIndex);
      dot.setAttribute('aria-selected', String(i === realIndex));
      dot.setAttribute('tabindex', i === realIndex ? '0' : '-1');
    }
  }

  #announce(realIndex) {
    if (this.#liveRegion) {
      this.#liveRegion.textContent = `Slide ${realIndex + 1} de ${this.#total}`;
    }
  }

  /* ---- IntersectionObserver ---- */

  #observeViewport() {
    this.#observer = new IntersectionObserver((entries) => {
      this.#isVisible = entries[0]?.isIntersecting ?? true;
      this.#isVisible ? this.#startAutoplay() : this.#stopAutoplay();
    }, { threshold: 0.1 });
    this.#observer.observe(this);
  }

  /* ---- Resize ---- */

  // El track se posiciona con translateX en PIXELES, calculados a partir del ancho del
  // viewport. Si ese ancho cambia sin que el slider se entere, el slide activo queda
  // descolocado.
  //
  // El listener de `resize` de abajo cubre el caso clasico (redimensionar la ventana) y
  // por eso debouncea. Pero el ancho tambien cambia SIN que la ventana se mueva: el push
  // drawer encoge la pagina. Ahi el debounce se nota — el hero salta a su sitio 150ms
  // despues de que el drawer ya asento. De ahi este observer: sigue la transicion
  // fotograma a fotograma y el slide se mantiene centrado durante todo el encogido.
  //
  // ⚠️ Y AQUI ESTA EL CUIDADO: esto corre en CADA FOTOGRAMA de la transicion. Si midiese
  // con `offsetWidth` / `getComputedStyle()`, forzaria un relayout SINCRONO justo antes de
  // escribir el transform — leer, escribir, leer, escribir. Eso es layout thrashing: el
  // navegador recalcula la maqueta dos veces por frame en vez de una, y con una pagina tan
  // grande como el home la transicion se arrastra.
  //
  // Por eso NO se mide nada aqui. El ResizeObserver ya trae los anchos medidos
  // (`borderBoxSize`), gratis y sin forzar layout: se observan el viewport y un slide, y
  // el resto es aritmetica. El gap va cacheado (#measureLayout lo dejo puesto al arrancar).
  // Solo se escribe.
  #observeSize() {
    const anchoDe = (entry) =>
      entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;

    this.#sizeObserver = new ResizeObserver((entries) => {
      let cambio = false;

      for (const entry of entries) {
        const ancho = anchoDe(entry);

        if (entry.target === this.#viewport) {
          if (ancho !== this.#vpW) { this.#vpW = ancho; cambio = true; }
        } else if (ancho !== this.#slideW) {
          this.#slideW = ancho;
          cambio = true;
        }
      }

      // La altura no descoloca nada: solo importa el ancho.
      if (!cambio) return;

      // #applyOffset y no #setPosition: el slide activo no ha cambiado, asi que no hay que
      // tocar clases, dots ni anunciar nada al lector de pantalla. Solo reposicionar.
      this.#applyOffset(this.#calcOffset(this.#index + 1), false);
    });

    this.#sizeObserver.observe(this.#viewport);

    const slide = this.#allSlides[1] ?? this.#allSlides[0];
    if (slide) this.#sizeObserver.observe(slide);
  }

  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => {
      this.#measureLayout();
      this.#setPosition(this.#index, false);
    }, 150);
  };
}

if (!customElements.get('hero-slider')) {
  customElements.define('hero-slider', HeroSlider);
}
